import { mdiCookie } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useState } from "react";
import styled from "styled-components";
import { CookieSettings, useCookies } from "./CookieProvider";

const StyledCookieButton = styled.div`
    position: fixed;
    bottom: 10px;
    left: 10px;
    z-index: 9999;
    user-select: none;

    & > div {
        position: relative;
        background: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.black};
        width: 60px;
        height: 60px;
        display: grid;
        align-items: center;
        justify-items: center;
        border-radius: 100%;
        cursor: pointer;
    }
`;

const Container = styled.div<{ $visible: boolean }>`
    position: absolute;
    cursor: initial;
    max-width: 500px;
    height: auto;
    background: ${({ theme }) => theme.colors.gray};
    bottom: 100%;
    left: 0px;
    margin: 0.5em 0px;
    padding: 1em;
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.colors.darkGray};
    font-size: 18px;
    white-space: nowrap;
    transform: ${({ $visible }) =>
        $visible ? "transform: translate(0, 0)" : "translate(-110%, 0)"};
    transition: transform 0.2s cubic-bezier(0.68, -0.55, 0.27, 1.55);

    & > div {
        display: grid;
        grid-template-columns: auto min-content;
        gap: 0px 0.5em;
        align-items: center;
    }
`;

function getCookieName(cookieName: keyof CookieSettings) {
    switch (cookieName) {
        case "googleMaps":
            return "Google Maps";
    }
}

export const CookieManagement: React.FC = () => {
    const { cookies, setCookie } = useCookies();
    const [visible, setVisible] = useState(false);

    return (
        <>
            <StyledCookieButton>
                <div onClick={() => setVisible((i) => !i)}>
                    <Icon path={mdiCookie} size="80%" />
                    <Container
                        $visible={visible}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span>
                            <b>Ihre Cookie-Einstellungen:</b>
                        </span>
                        <div>
                            {(
                                Object.entries(cookies) as [
                                    keyof CookieSettings,
                                    boolean,
                                ][]
                            ).map(([name, value]) => {
                                const id = `${name}-toggle`;
                                return (
                                    <React.Fragment key={name}>
                                        <label
                                            htmlFor={id}
                                            onClick={() =>
                                                setCookie(name, !value)
                                            }
                                        >
                                            {getCookieName(name)}:{" "}
                                        </label>
                                        <Toggle
                                            id={id}
                                            value={value}
                                            onChange={(value) =>
                                                setCookie(name, value)
                                            }
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </Container>
                </div>
            </StyledCookieButton>
        </>
    );
};

const StyledToggle = styled.div<{ $value: boolean }>`
    position: relative;
    height: 20px;
    width: 40px;
    background: ${({ theme }) => theme.colors.darkGray};
    border-radius: 10px;
    cursor: pointer;

    & div {
        position: absolute;
        background: ${({ theme, $value }) =>
            $value ? theme.colors.accent : theme.colors.white};
        width: 20px;
        height: 100%;
        border-radius: inherit;
        left: ${({ $value }) => ($value ? "20px" : "0px")};
        transition:
            left 0.1s linear,
            background 0.1s linear;
    }

    & input {
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
    }
`;

const Toggle: React.FC<{
    id: string;
    value: boolean;
    onChange: (value: boolean) => void;
}> = ({ id, value, onChange }) => {
    return (
        <StyledToggle $value={value} onClick={() => onChange(!value)}>
            <div></div>
            <input id={id} type="checkbox" value={value.toString()} />
        </StyledToggle>
    );
};
